import { v4 as uuidv4 } from 'uuid';
import cloneDeep from 'lodash/cloneDeep';
import Module from '@/store/resource/common/module';
import CustomAgents from '@/store/resource/customAgents';
import api from '@/factories/api';

function createEmptyDraft() {
  return {
    name: '',
    icon: 'smart_toy',
    description: '',
    instructions: '',
    goal: '',
    stop_conditions: '',
    status: 'active',
    tools: [],
    model: '',
  };
}

function toArray(value) {
  return Array.isArray(value) ? value : [];
}

// Test-console calls get an explicit timeout: the app's axios factory sets
// none (axios default = wait forever), so one request stalled by a dying
// connection would wedge the console's busy flags permanently (C1b fix).
// All five calls are fast server-side (evals are dispatched to a queue, not
// awaited), so 20s is generous.
const CONSOLE_API_TIMEOUT_MS = 20000;

function catalogOrder(tool) {
  const order = Number(tool && tool.order);
  return Number.isFinite(order) ? order : Number.MAX_SAFE_INTEGER;
}

function normalizeTool(tool, configSchema = {}) {
  return {
    tool_uuid: tool.tool_uuid,
    name: tool.name,
    label: tool.label || tool.name,
    summary: tool.summary || '',
    description: tool.description || '',
    allows_multiple_instances: Boolean(tool.allows_multiple_instances),
    group: tool.group || 'General',
    icon: tool.icon || 'build',
    fontIcon: tool.font_icon !== false,
    order: catalogOrder(tool),
    configSchema,
  };
}

function normalizeToolInstance(tool) {
  return {
    instance_uuid: tool.instance_uuid || uuidv4(),
    tool_uuid: tool.tool_uuid,
    config: tool.config || {},
  };
}

function normalizeAgentDraft(agent) {
  if (!agent) return createEmptyDraft();
  return {
    ...agent,
    tools: toArray(agent.tools).map(normalizeToolInstance),
  };
}

function normalizeAvailableTools(catalog = [], schemas = {}) {
  return toArray(catalog)
    .filter((tool) => !tool.system)
    .map((tool) => normalizeTool(tool, schemas[tool.name]?.configSchema));
}

function normalizePayload(payload = {}) {
  const normalized = { ...payload };
  if (
    Object.prototype.hasOwnProperty.call(normalized, 'instructions') &&
    typeof normalized.instructions === 'string' &&
    normalized.instructions.trim() === ''
  ) {
    delete normalized.instructions;
  }
  return normalized;
}

let versionsFetchToken = 0;

const module = new Module(new CustomAgents());

module.add('state', {
  draft: null,
  availableTools: [],
  versions: [],
  versionsAgentId: null,
  versionsLoaded: false,
  publishedVersion: null,
  publishedVersionSnapshot: null,
  publishBaseline: null,
  compileResult: null,
  compiling: false,
  publishing: false,
  reviewAutosaving: false,
  reviewPreviewDirty: false,
  testContactId: '',
  testVersionSource: 'head',
});

module.add('mutations', {
  setDraft(state, agent) {
    const previousId = state.draft?.id;
    const nextId = agent?.id;
    if (previousId && previousId !== nextId) {
      state.testContactId = '';
      state.testVersionSource = 'head';
    }
    state.draft = normalizeAgentDraft(agent);
  },
  setDraftField(state, { key, value }) {
    state.draft = { ...(state.draft || createEmptyDraft()), [key]: value };
  },
  setAvailableTools(state, tools) {
    state.availableTools = tools;
  },
  setVersions(state, versions) {
    state.versions = versions;
    state.publishedVersion = versions.find((v) => v.is_active) || null;
    state.versionsLoaded = true;
  },
  resetVersionsLoaded(state) {
    state.versionsLoaded = false;
  },
  clearVersionsForAgent(state, agentId) {
    state.versions = [];
    state.publishedVersion = null;
    state.publishedVersionSnapshot = null;
    state.versionsAgentId = agentId;
  },
  setPublishedVersionSnapshot(state, snapshot) {
    state.publishedVersionSnapshot = snapshot;
  },
  setPublishBaseline(state, baseline) {
    state.publishBaseline = baseline ? cloneDeep(baseline) : null;
  },
  setCompiling(state, val) {
    state.compiling = val;
  },
  setPublishing(state, val) {
    state.publishing = val;
  },
  setReviewAutosaving(state, val) {
    state.reviewAutosaving = val;
  },
  setReviewPreviewDirty(state, val) {
    state.reviewPreviewDirty = val;
  },
  setCompileResult(state, result) {
    state.compileResult = result;
  },
  resetReviewState(state) {
    state.compileResult = null;
    state.compiling = false;
    state.publishing = false;
    state.reviewAutosaving = false;
    state.reviewPreviewDirty = false;
  },
  setTestContactId(state, contactId) {
    state.testContactId = contactId;
  },
  setTestVersionSource(state, versionSource) {
    state.testVersionSource = versionSource;
  },
});

module.add('actions', {
  initNewDraft({ commit }) {
    commit('setDraft', null);
    commit('resetReviewState');
  },

  async fetchAll({ dispatch }) {
    return dispatch('load');
  },

  async fetchOne({ dispatch }, id) {
    return dispatch('loadOne', { path: id });
  },

  async create({ dispatch }, payload) {
    return dispatch('createOne', normalizePayload(payload));
  },

  async update({ dispatch, getters }, { id, payload }) {
    const current = getters.byId(id) || {};
    const normalized = normalizePayload(payload);
    const model = { ...current, ...normalized, id };
    delete model.manifest;
    delete model.published_version_count;
    delete model.published_version_id;
    delete model.rollout;
    await dispatch('save', { model });
    return getters.byId(id) || null;
  },

  async fetchAvailableTools({ commit }) {
    try {
      const [catalog, schemaData] = await Promise.all([
        api().get('agent/tools'),
        api().get('agent/tools/schema'),
      ]);
      const tools = normalizeAvailableTools(catalog, schemaData?.schema || {});
      commit('setAvailableTools', tools);
      return tools;
    } catch {
      commit('setAvailableTools', []);
      return [];
    }
  },

  async fetchAgentContacts(
    _,
    { agentId, status = 'active', needsGuidance = false }
  ) {
    const result = await api().get(`agent/custom/contacts/${agentId}`, {
      params: { status, ...(needsGuidance ? { needs_guidance: 1 } : {}) },
    });
    return toArray(result);
  },

  async fetchAgentLogs(
    _,
    {
      agentId = null,
      instanceId = null,
      contactId = null,
      type = null,
      page = 1,
      perPage = 50,
    }
  ) {
    if (!instanceId && !agentId) {
      return {
        page,
        per_page: perPage,
        count: 0,
        has_more: false,
        results: [],
      };
    }

    const params = { page, per_page: perPage };
    if (instanceId) {
      params.instance_id = instanceId;
    } else {
      params.agent_id = agentId;
    }
    if (contactId) params.contact_id = contactId;
    if (type) params.type = type;

    const result = await api({ timeout: CONSOLE_API_TIMEOUT_MS }).get(
      'agent/logs',
      { params }
    );

    return result && Array.isArray(result.results)
      ? result
      : { page, per_page: perPage, count: 0, has_more: false, results: [] };
  },

  async consoleEvaluate(
    _,
    { customAgentId, contactId, versionSource = 'head' }
  ) {
    return api({
      timeout: CONSOLE_API_TIMEOUT_MS,
    }).post('agent/console/evaluate', {
      custom_agent_id: customAgentId,
      contact_id: contactId,
      version_source: versionSource || 'head',
    });
  },

  async consoleInbound(
    _,
    { customAgentId, contactId, body, versionSource = 'head' }
  ) {
    return api({
      timeout: CONSOLE_API_TIMEOUT_MS,
    }).post('agent/console/inbound', {
      custom_agent_id: customAgentId,
      contact_id: contactId,
      body,
      version_source: versionSource || 'head',
    });
  },

  async consoleStatus(_, { customAgentId, contactId }) {
    return api({ timeout: CONSOLE_API_TIMEOUT_MS, skipErrorModal: true }).get(
      'agent/console/status',
      { params: { custom_agent_id: customAgentId, contact_id: contactId } }
    );
  },

  async consoleReset(_, { customAgentId, contactId }) {
    return api({ timeout: CONSOLE_API_TIMEOUT_MS }).post(
      'agent/console/reset',
      {
        custom_agent_id: customAgentId,
        contact_id: contactId,
      }
    );
  },

  async consoleTime(_, { customAgentId, contactId, seconds }) {
    return api().post('agent/console/time', {
      custom_agent_id: customAgentId,
      contact_id: contactId,
      seconds,
    });
  },

  async fetchConsoleMessages(_, { customAgentId, contactId }) {
    return api({ timeout: CONSOLE_API_TIMEOUT_MS }).get(
      'agent/console/messages',
      {
        params: { custom_agent_id: customAgentId, contact_id: contactId },
      }
    );
  },

  async enrollContact(_, { agentId, contactId, ifTerminal = 'resume' }) {
    return api({ skipErrorModal: true }).post('agent/custom/enroll', {
      custom_agent_id: agentId,
      contact_id: contactId,
      if_terminal: ifTerminal,
    });
  },

  async unenrollContact(_, { agentId, contactId }) {
    return api().post('agent/custom/unenroll', {
      custom_agent_id: agentId,
      contact_id: contactId,
    });
  },

  async resetContact(_, { agentId, contactId }) {
    return api().post('agent/custom/reset', {
      custom_agent_id: agentId,
      contact_id: contactId,
    });
  },

  async reevaluateContact(_, { agentId, contactId }) {
    return api().post('agent/custom/reevaluate', {
      custom_agent_id: agentId,
      contact_id: contactId,
    });
  },

  async fetchContactInstances(_, contactId) {
    const result = await api().get(
      `agent/custom/contact-instances/${contactId}`
    );
    return toArray(result);
  },

  async fetchAgentIssueReplies(_, instanceId) {
    const result = await api({ skipErrorModal: true }).get(
      `agent/custom/instances/${instanceId}/replies`
    );
    return toArray(result);
  },

  submitAgentIssue(_, { instanceId, sourceLogId, comment }) {
    return api({ skipErrorModal: true }).post('agent/custom/issues', {
      instance_id: instanceId,
      source_log_id: sourceLogId,
      comment,
    });
  },

  async fetchNeedsGuidance(_, { page = 1, perPage = 25 } = {}) {
    // skipErrorModal — this is polled every 45s; the caller owns error UX.
    const result = await api({ skipErrorModal: true }).get(
      'agent/custom/needs-guidance',
      { params: { page, per_page: perPage } }
    );

    return result && Array.isArray(result.results)
      ? result
      : { page, per_page: perPage, count: 0, has_more: false, results: [] };
  },

  async resolveGuidance(_, { instanceId, message }) {
    return api({ skipErrorModal: true }).post('agent/custom/resolve-guidance', {
      agent_id: instanceId,
      message,
    });
  },

  async resolveGuidanceBulk(_, { instanceIds, message }) {
    const result = await api({ skipErrorModal: true }).post(
      'agent/custom/resolve-guidance',
      {
        agent_ids: instanceIds,
        message,
      }
    );
    if (!Array.isArray(result?.results)) {
      throw new Error('Invalid bulk resolve response');
    }
    return result;
  },

  async remove({ dispatch, getters }, id) {
    const record = getters.byId(id);
    if (!record) return;
    await dispatch('deleteOne', record);
  },

  async fetchVersions({ commit, dispatch, state }, id) {
    // Another agent's versions must not show (e.g. its rollout) while this one loads
    if (state.versionsAgentId !== id) commit('clearVersionsForAgent', id);
    commit('resetVersionsLoaded');
    const token = ++versionsFetchToken;
    const isStale = () => token !== versionsFetchToken;

    let versions = [];
    try {
      versions = toArray(await api().get(`agent/custom/${id}/versions`));
    } catch {
      if (!isStale()) {
        commit('setVersions', []);
        commit('setPublishedVersionSnapshot', null);
      }
      return [];
    }

    if (isStale()) return [];
    commit('setVersions', versions);

    const active = versions.find((v) => v.is_active);
    if (!active) {
      commit('setPublishedVersionSnapshot', null);
      return versions;
    }

    try {
      const snapshot = await dispatch('fetchVersion', {
        id,
        versionId: active.version_id,
      });
      if (!isStale()) commit('setPublishedVersionSnapshot', snapshot);
    } catch {
      if (!isStale()) commit('setPublishedVersionSnapshot', null);
    }

    return versions;
  },

  async fetchVersion(_, { id, versionId }) {
    return api().get(`agent/custom/${id}/versions/${versionId}`);
  },

  async compilePreview({ commit }, payload) {
    const { id, force = false } =
      typeof payload === 'object' && payload !== null
        ? payload
        : { id: payload };
    commit('setCompiling', true);
    try {
      const result = await api().post(`agent/custom/${id}/compile-preview`, {
        force,
      });
      commit('setCompileResult', result);
      return result;
    } finally {
      commit('setCompiling', false);
    }
  },

  async publish({ commit }, { id, manifest, description, rolloutPercent }) {
    commit('setPublishing', true);
    try {
      const result = await api().post(`agent/custom/${id}/publish`, {
        manifest,
        description,
        ...(rolloutPercent < 100 ? { rollout_percent: rolloutPercent } : {}),
      });
      commit('setPublishBaseline', null);
      commit('setReviewPreviewDirty', false);
      return result;
    } finally {
      commit('setPublishing', false);
    }
  },

  async activateVersion(
    { dispatch, commit, state },
    { id, versionId, currentDraft = null }
  ) {
    const baseline = currentDraft || state.draft;
    await api().post(`agent/custom/${id}/versions/${versionId}/activate`, {});
    const versions = await dispatch('fetchVersions', id);
    commit('setPublishBaseline', baseline);
    return versions;
  },

  async setRollout({ dispatch }, { id, percent }) {
    await api().post(`agent/custom/${id}/rollout`, { percent });
    return dispatch('fetchVersions', id);
  },

  async deleteVersion({ commit, dispatch }, { id, versionId }) {
    await api().delete(`agent/custom/${id}/versions/${versionId}`);
    commit('setPublishBaseline', null);
    return dispatch('fetchVersions', id);
  },
});

export default module.create();
